import { UserCredentialAuthenticationRequest } from '@/api/models/ace/xFound/Portal/Core/CAQ/Authentication/Requests/user-credential-authentication-request';
import { LoadingAnimationService } from '@/core/services/loading-animation.service';
import { AccessToken } from '@/core/models/access-token.model';
import { IdToken } from '@/core/models/id-token.model';
import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TextBoxComponent } from '@progress/kendo-angular-inputs';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { AuthenticationConfig } from '../../authentication.module';
import { ExtendedAuthenticationService } from '../../services/extended-authentication.service';
import { AUTHENTICATION_CONFIG } from '../../injection-tokens';
import { UserSettingsService } from '@/api/services/user-settings.service';
import { I18nService } from '@/core/services/i18nService';
import { LocalizedLinkDisplayModel, UserSettingsDisplayModel } from '@/api/model-index';
import { emailValidator } from '@/core/validators/customform.validators';
import { StammdatenService } from '@/core/services/stammdaten.service';

@Component({
  selector: 'login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, AfterViewInit {
  logoUrl: string = "/assets/ace_logo_xfound_header.png";

  error: string;
  loginForm: UntypedFormGroup;
  redirect : string;
  loginSubscription : Subscription;

  @ViewChild("password") public passwordTextBox: TextBoxComponent;

  public get isAnmeldeButtonDisabled(): boolean {
    return this.loginForm.invalid || this.isLoading;
  }

  public get benutzernameValidated() : boolean {
    return this.loginForm.controls.benutzername.valid || this.loginForm.controls.benutzername.untouched;
  }

  public get passwortValidated() : boolean {
    return this.loginForm.controls.passwort.valid || this.loginForm.controls.passwort.untouched;
  }

  public get passwortError() : ValidationErrors | null {
    return this.loginForm.controls.passwort.errors
  }

  public get isLoading() : boolean{
    return this.loginSubscription && !this.loginSubscription.closed;
  }

  public get benutzernameFormControl(): AbstractControl {
    return this.loginForm.controls["benutzername"];
  }

  public get passwortFormControl(): AbstractControl {
    return this.loginForm.controls["passwort"];
  }

  public get benutzernameErrorMessages(): string[] {
    let validationErrors: ValidationErrors | null = this.benutzernameFormControl.errors;

    if(!validationErrors)
      return [];

    return this.toBenutzernameErrorMessage(validationErrors);
  }

  public get passwortErrorMessages(): string[] {
    let validationErrors: ValidationErrors | null = this.passwortFormControl.errors;

    if(!validationErrors)
      return [];

    return this.toPasswortErrorMessage(validationErrors);
  }

  public get isRegistrationDisabled(): boolean {
    return this.stammdatenService.Portalkonfiguration.registrationIsDisabled == true;
  }


  constructor(protected router: Router,
    protected route: ActivatedRoute,
    protected formBuilder: UntypedFormBuilder,
    protected authenticationService: ExtendedAuthenticationService,
    protected loadingAnimationService: LoadingAnimationService,
    protected usersettingsService: UserSettingsService,
    protected languageservice: I18nService,
    protected stammdatenService: StammdatenService,
    @Inject(AUTHENTICATION_CONFIG) protected authenticationConfig: AuthenticationConfig) {

    this.createForm();
  }
  ngAfterViewInit(): void {
    this.passwordTextBox.input.nativeElement.type = "password";
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(
      params => this.redirect = params.redirect
    );
  }

  public login(){
    let loginCommand = new UserCredentialAuthenticationRequest();
    loginCommand.username = this.loginForm.get("benutzername")?.value;
    loginCommand.password = this.loginForm.get("passwort")?.value;
    loginCommand.scope = this.authenticationConfig.scope;
    
    this.loginSubscription = this.authenticationService.login(loginCommand)
    .pipe(finalize(() => {
        this.loginForm.markAsPristine();
    }))
    .subscribe(idToken => {
      if (idToken)
        this.authenticationService.saveIdToken(new IdToken(idToken))
        
    
        this.usersettingsService.apiUserSettingsGet().subscribe(res => {
          if(res.userSettings?.sprache)
            this.languageservice.SetCurrLanguageFromEnum(res.userSettings.sprache);
          else{
            var settings = new UserSettingsDisplayModel();
            settings.sprache = this.languageservice.GetCurrLanguageAsEnum();
            this.usersettingsService.apiUserSettingsPost$Response({body : settings})
          }
           
        })
    
        let promise = this.router.navigate([this.redirect || this.authenticationConfig.defaultRedirectUrl], {replaceUrl: true});
      this.loadingAnimationService.showSpinnerUsingPromise(promise);
    });

    this.loadingAnimationService.showSpinnerUsingSubscription(this.loginSubscription);
  }

  protected toBenutzernameErrorMessage(validationErrors: ValidationErrors) : string[] {
    let errorMessages: string[] = [];

    for(let key in validationErrors){
      switch(key){
        case "required":
          errorMessages.push(this.languageservice.instant("register.email_required"));
          break;
        case "email":
          errorMessages.push(this.languageservice.instant("register.email_invalid"));
            break;
        default:
          errorMessages.push(this.languageservice.instant("register.invalid"));
      }
    }

    return errorMessages;
  }

  protected toPasswortErrorMessage(validationErrors: ValidationErrors) : string[] {
    let errorMessages: string[] = [];

    for(let key in validationErrors){
      switch(key){
        case "required":
          errorMessages.push(this.languageservice.instant("register.password_required"));
          break;
        case "minlength":
          errorMessages.push(this.languageservice.instant("register.passwordformat_lenght_required"));
          break;
        case "notStrongEnough":
          errorMessages.push(this.languageservice.instant("register.passwordformat_strenght_required"));
            break;
        default:
          errorMessages.push(this.languageservice.instant("register.invalid"));
      }
    }

    return errorMessages;
  }

  protected createForm() {
    this.loginForm = this.formBuilder.group({
      benutzername: ['', [Validators.required, emailValidator]],
      passwort: ['', [Validators.required]]
    });
  }
}
